
import { useEffect, useRef } from "react";
import {
  FeatherInstagram,
  FeatherFacebook,
  FeatherLinkedin,
  FeatherLock,
  FeatherShield,
} from "@subframe/core";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import brandLogo from "../assets/brandlogo2.png"
import SecurityNotice from "./SecurityNotice";

gsap.registerPlugin(ScrollTrigger);

const Footer = () => {
  const footerRef = useRef<HTMLDivElement>(null);
  const sectionsRef = useRef<(HTMLDivElement | null)[]>([]);

  const getBrandLogo = () => {
    return brandLogo;
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Staggered entry for footer sections
      gsap.from(sectionsRef.current, {
        y: 60,
        opacity: 0,
        duration: 1.2,
        stagger: 0.15,
        ease: "power4.out",
        scrollTrigger: {
          trigger: footerRef.current,
          start: "top 85%",
        },
        clearProps: "all"
      });

      // Subtle parallax for background elements
      gsap.to(".footer-glow", {
        y: -50,
        scrollTrigger: {
          trigger: footerRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 1
        }
      });
    }, footerRef);

    return () => ctx.revert();
  }, []);

  return (
    <>
      <SecurityNotice />
      <footer ref={footerRef} className="w-full relative py-32 overflow-hidden bg-text-primary text-white border-t-8 border-secondary">
        {/* Structural Background Accents */}
        <div className="absolute top-0 right-0 w-1/3 h-full bg-white opacity-[0.02] -skew-x-12 translate-x-1/2 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-1/4 h-1/2 bg-secondary opacity-[0.05] skew-x-12 -translate-x-1/4 pointer-events-none" />

        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 mb-24">

            {/* Brand Identity Section */}
            <div ref={(el) => { sectionsRef.current[0] = el; }} className="lg:col-span-5 space-y-12">
              <div className="flex flex-col gap-8">
                <div className="p-4 bg-white/95 backdrop-blur-sm border border-white/20 w-fit shadow-[0_20px_50px_rgba(0,0,0,0.5)] transition-all duration-300 hover:shadow-[0_20px_60px_rgba(0,0,0,0.6)] group">
                  <img src={getBrandLogo()} alt={import.meta.env.VITE_COMPANY_NAME} className="h-24 w-fit object-contain transition-transform duration-500 group-hover:scale-[1.02]" />
                </div>
                <p className="text-xl font-bold text-white/70 leading-relaxed max-w-md border-l-4 border-secondary pl-8">
                  Engineered for the modern digital economy. Providing <span className="text-secondary">institutional-grade</span> liquidity with radical transparency.
                </p>
              </div>

              <div className="flex gap-4">
                {[
                  { icon: <FeatherLinkedin />, href: import.meta.env.VITE_COMPANY_LINKEDIN_URL, label: "LinkedIn" },
                  { icon: <FeatherFacebook />, href: import.meta.env.VITE_COMPANY_FACEBOOK_URL, label: "Facebook" },
                  { icon: <FeatherInstagram />, href: import.meta.env.VITE_COMPANY_INSTAGRAM_URL, label: "Instagram" }
                ].map((social, i) => (
                  <a
                    key={i}
                    href={social.href}
                    target="_blank"
                    className="w-14 h-14 bg-white/5 border border-white/10 flex items-center justify-center text-white/50 hover:text-secondary hover:border-secondary hover:bg-white/10 transition-all duration-300"
                    aria-label={social.label}
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Links Bento */}
            <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-12">
              <div ref={(el) => { sectionsRef.current[1] = el; }} className="space-y-8">
                <h3 className="text-[10px] font-black text-secondary uppercase tracking-[0.4em]">Services</h3>
                <ul className="space-y-5">
                  {[
                    { label: "Loan Calculator", href: "/loan-calculator" },
                    { label: "About Us", href: "/about" },
                    { label: "FAQ Center", href: "/faq" },
                    { label: "Contact Us", href: "/contact-us" }
                  ].map((link) => (
                    <li key={link.label}>
                      <a href={link.href} className="text-white/60 font-black uppercase tracking-widest hover:text-secondary transition-colors text-[10px]">{link.label}</a>
                    </li>
                  ))}
                </ul>
              </div>

              <div ref={(el) => { sectionsRef.current[2] = el; }} className="space-y-8">
                <h3 className="text-[12px] font-black text-secondary uppercase tracking-[0.2em]">Regulatory</h3>
                <ul className="space-y-5">
                  {[
                    { label: "Privacy Policy", href: "/privacy-policy" },
                    { label: "Terms of Service", href: "/terms-condition" },
                    { label: "Corporate Governance", href: "/corporate-governance" },
                    { label: "Interest Rate Policy", href: "/interest-rate-policy" },
                    { label: "Grievance Redressal", href: "/grievance-redressal-policy" },
                    { label: "Fair Practice Code", href: "/fair-practice-code" },
                    { label: "KYC Policy", href: "/kyc-aml-policy" },
                  ].map((link) => (
                    <li key={link.label}>
                      <a href={link.href} className="text-white/80 font-bold hover:text-secondary transition-colors text-[13px]">{link.label}</a>
                    </li>
                  ))}
                </ul>
              </div>

              <div ref={(el) => { sectionsRef.current[3] = el; }} className="space-y-8">
                <h3 className="text-[10px] font-black text-secondary uppercase tracking-[0.4em]">Terminal</h3>
                <div className="space-y-6">
                  <div className="group">
                    <div className="text-[9px] font-black text-white/30 uppercase tracking-[0.3em] mb-2">Endpoint</div>
                    <a href={`mailto:${import.meta.env.VITE_COMPANY_EMAIL}`} className="text-white font-black hover:text-secondary transition-colors block text-sm tracking-tighter">
                      {import.meta.env.VITE_COMPANY_EMAIL}
                    </a>
                  </div>
                  <div className="group">
                    <div className="text-[9px] font-black text-white/30 uppercase tracking-[0.3em] mb-2">Support</div>
                    <span className="text-white font-black block text-sm tracking-tighter">{import.meta.env.VITE_COMPANY_PHONE}</span>
                  </div>
                  <div className="group">
                    <div className="text-[9px] font-black text-white/30 uppercase tracking-[0.3em] mb-2">Facility</div>
                    <span className="text-white font-black block text-xs leading-relaxed tracking-tight">{import.meta.env.VITE_COMPANY_ADDRESS}</span>
                  </div>
                  <div className="pt-4">
                    <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/5 border border-white/10 text-secondary text-[10px] font-black uppercase tracking-[0.3em]">
                      <div className="w-2 h-2 bg-secondary animate-pulse" />
                      Live: 24/7/365
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar - Certification & Support */}
          <div ref={(el) => { sectionsRef.current[4] = el; }} className="pt-16 border-t border-white/10">
            <div className="flex flex-col md:flex-row items-center justify-between gap-12 text-white/40">
              <div className="flex flex-wrap justify-center md:justify-start gap-10 items-center">
                <div className="flex items-center gap-4 bg-white/5 border border-white/10 px-6 py-3 shadow-2xl">
                  <FeatherShield className="text-secondary text-[20px]" />
                  <span className="text-[10px] font-black uppercase tracking-[0.3em] text-white">
                    RBI Licensed NBFC: <span className="text-secondary">{import.meta.env.VITE_COMPANY_NBFC_LICENSE}</span>
                  </span>
                </div>
                <div className="flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.3em] hover:text-white transition-colors cursor-default">
                  <FeatherLock className="text-[16px]" /> Tier 1 Encryption
                </div>
              </div>

              <div className="text-center md:text-right space-y-2">
                <div className="text-[10px] font-black text-white uppercase tracking-[0.4em]">
                  © {new Date().getFullYear()} {import.meta.env.VITE_COMPANY_NAME}
                </div>
                <div className="text-[9px] font-black text-white/30 uppercase tracking-[0.2em]">
                  Powered By {import.meta.env.VITE_COMPANY_NBFC}
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>

    </>
  );
};

export default Footer;

